import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import { articleSource, articleToOption } from "@/lib/autocompleteSources";
import { CHARGE_RULE_LABELS } from "@/lib/chargeRuleLabels";
import type { Article, ChargeRule } from "@/types/camping";

const RULE_LABELS = CHARGE_RULE_LABELS;

interface RuleFormState {
  article_id: string;
  is_active: boolean;
}

function ruleToForm(rule: ChargeRule): RuleFormState {
  return {
    article_id: rule.article_id,
    is_active: rule.is_active,
  };
}

export interface EditChargeRuleModalProps {
  open: boolean;
  rule: ChargeRule | null;
  articles: Article[];
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditChargeRuleModal({
  open,
  rule,
  articles,
  onClose,
  onSaved,
  canEdit,
}: EditChargeRuleModalProps) {
  const [form, setForm] = useState<RuleFormState | null>(null);
  const [articleOption, setArticleOption] = useState<AutocompleteOption<Article> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadArticleOptions = useCallback(
    (query: string) => articleSource(articles, query),
    [articles],
  );

  useEffect(() => {
    if (!open || !rule) return;
    setForm(ruleToForm(rule));
    const article = articles.find((a) => a.id === rule.article_id);
    setArticleOption(article ? articleToOption(article) : null);
    setError(null);
    setSubmitting(false);
  }, [open, rule, articles]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!rule || !form || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/charge-rules/${rule.id}`, {
        article_id: form.article_id,
        is_active: form.is_active,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!rule || !form) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={RULE_LABELS[rule.rule_type] ?? rule.rule_type}
      description={canEdit ? "Zuordnung bearbeiten" : "Zuordnungsdetails (nur Lesen)"}
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        {rule.pitch_category_name ? (
          <p className="text-sm text-label-secondary">
            Kategorie:{" "}
            <span className="font-medium text-label">{rule.pitch_category_name}</span>
          </p>
        ) : null}
        <LockedAutocomplete
          label="Artikel"
          required
          disabled={!canEdit}
          placeholder="Artikelnummer oder Bezeichnung suchen…"
          value={articleOption}
          loadOptions={loadArticleOptions}
          onChange={(option) => {
            setArticleOption(option);
            setForm((f) => (f ? { ...f, article_id: option?.value.id ?? "" } : f));
          }}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.is_active}
            onChange={(e) => setForm((f) => (f ? { ...f, is_active: e.target.checked } : f))}
          />
          Aktiv
        </label>
        {rule ? (
          <AuditHistory entityType="charge_rule" entityId={rule.id} active={open} />
        ) : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {canEdit ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
